import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import AssignmentIcon from "@material-ui/icons/Assignment";
import Autocomplete from "@material-ui/lab/Autocomplete/Autocomplete";
import React, { useEffect, useMemo, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import { getCustomers } from "../../../../api/customer";
import {
  deleteObraHeader,
  getAvailableOfferHeadersForObra,
  getObraHeaders,
  postObraHeaderFromOffer,
} from "../../../../api/obraHeader";
import { getObraStatus } from "../../../../api/obraStatus";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import FiltersCard from "../../../components/filters/Filter";
import Table, {
  buttonsStyle,
  getCurrentTablePage,
} from "../../../components/tables/table";

const initialFilters = {
  cliente: null,
  estado: null,
};

function formatDate(cell) {
  if (!cell) return "";
  const date = new Date(cell);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getDate()).padStart(2, "0")}/${String(
    date.getMonth() + 1
  ).padStart(2, "0")}/${date.getFullYear()}`;
}

function obraStatusFormatter(status) {
  if (!status) return null;

  return status?.descripcion || "";
}

function planningStatusFormatter(status) {
  if (!status?.descripcion) return "";

  let backgroundColor = "#E4E6EF";
  let textColor = "#3F4254";

  if (status.descripcion === "A tiempo") {
    backgroundColor = "#C9F7F5";
    textColor = "#0BB783";
  } else if (status.descripcion === "Warning") {
    backgroundColor = "#FFF4DE";
    textColor = "#FFA800";
  } else if (status.descripcion === "Retraso") {
    backgroundColor = "#FFE2E5";
    textColor = "#F64E60";
  }

  return (
    <div
      className="p-2 font-weight-bold"
      style={{
        backgroundColor,
        color: textColor,
        borderRadius: "0.42rem",
      }}
    >
      {status.descripcion.toUpperCase()}
    </div>
  );
}

function progressFormatter(value) {
  return `${new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))}%`;
}

function getOfferOptionLabel(offer, customers) {
  if (!offer) return "";
  const customerName =
    customers?.find((customer) => customer.id === offer.cliente_id)?.nombre || "";
  return `${offer.num_oferta || ""} - ${customerName} - ${offer.localizacion || ""}`;
}

function getData(obraHeaders, customers) {
  return obraHeaders.map((obraHeader) => ({
    id: obraHeader?.id,
    codigoObra: obraHeader?.codigo_obra || "",
    numOferta: obraHeader?.oferta_num_oferta || "",
    cliente:
      customers?.find((customer) => customer.id === obraHeader?.cliente_id)?.nombre ||
      "",
    localizacion: obraHeader?.localizacion || "",
    fechaInicio: formatDate(obraHeader?.fecha_inicio),
    fechaFin: formatDate(obraHeader?.fecha_fin),
    estado: obraHeader?.estado_obra,
    situacion: obraHeader?.situacion_planificacion,
    progreso: progressFormatter(obraHeader?.porcentaje_completado),
  }));
}

export default function ObrasPage() {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [obraStatus, setObraStatus] = useState([]);
  const [availableOffers, setAvailableOffers] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [filterOptions, setFilterOptions] = useState(initialFilters);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [creatingObra, setCreatingObra] = useState(false);
  const [selectedObra, setSelectedObra] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [newObraData, setNewObraData] = useState({
    codigo_obra: "",
    fecha_inicio: "",
    fecha_fin: "",
  });

  const history = useHistory();
  const location = useLocation();

  const loggedUser = useSelector(
    (store) => store.authentication?.user,
    shallowEqual
  );
  const accessToken = loggedUser?.accessToken;

  useEffect(() => {
    getCustomers(accessToken)
      .then((res) => {
        if (res.status === 200) {
          setCustomers(res.data);
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se han podido obtener los clientes.",
        });
      });

    getObraStatus(accessToken)
      .then((res) => {
        if (res.status === 200) {
          setObraStatus(res.data);
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se han podido obtener los estados de obra.",
        });
      });

    getObraHeaders(accessToken)
      .then((res) => {
        if (res.status === 200) {
          setData(res.data);
          setFilteredData(res.data);
          setRefresh(false);
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se han podido obtener las obras.",
        });
      });
  }, [refresh, accessToken]);

  const offerOptions = useMemo(
    () =>
      availableOffers.map((offer) => ({
        ...offer,
        label: getOfferOptionLabel(offer, customers),
      })),
    [availableOffers, customers]
  );

  const handleSearch = () => {
    setFilteredData(
      data.filter((item) => {
        let filter = true;

        if (filterOptions.cliente) {
          filter = filter && item?.cliente_id === filterOptions.cliente?.id;
        }

        if (filterOptions.estado) {
          filter = filter && item?.estado_obra_id === filterOptions.estado?.id;
        }

        return filter;
      })
    );
  };

  const handleClearFilters = () => {
    setFilterOptions(initialFilters);
    setFilteredData(data);
    setRefresh(true);
  };

  const handleOpenCreateDialog = () => {
    getAvailableOfferHeadersForObra(accessToken)
      .then((res) => {
        if (res.status === 200) {
          setAvailableOffers(res.data);
          setSelectedOffer(null);
          setNewObraData({
            codigo_obra: "",
            fecha_inicio: "",
            fecha_fin: "",
          });
          setOpenCreateDialog(true);
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se han podido obtener las ofertas disponibles para crear una obra.",
        });
      });
  };

  const handleCreateObra = () => {
    if (
      !selectedOffer?.id ||
      !newObraData.codigo_obra?.trim() ||
      !newObraData.fecha_inicio ||
      !newObraData.fecha_fin
    ) {
      return;
    }

    if (newObraData.fecha_fin < newObraData.fecha_inicio) {
      alertError({
        customMessage: "La fecha fin no puede ser anterior a la fecha inicio.",
      });
      return;
    }

    setCreatingObra(true);

    postObraHeaderFromOffer(
      selectedOffer.id,
      {
        codigo_obra: newObraData.codigo_obra.trim(),
        fecha_inicio: new Date(newObraData.fecha_inicio).toISOString(),
        fecha_fin: new Date(newObraData.fecha_fin).toISOString(),
      },
      accessToken
    )
      .then((res) => {
        if (res.status === 200) {
          alertSuccess({
            customMessage: "Obra creada con éxito.",
          });
          setOpenCreateDialog(false);
          history.push({
            pathname: `/obras/${res.data?.id}/edit`,
            state: {
              page: getCurrentTablePage(location),
            },
          });
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se ha podido crear la obra.",
        });
      })
      .finally(() => {
        setCreatingObra(false);
      });
  };

  const handleDeleteObra = () => {
    if (!selectedObra?.id) {
      return;
    }

    deleteObraHeader(selectedObra.id, accessToken)
      .then((res) => {
        if (res.status === 204 || res.status === 200) {
          alertSuccess({
            customMessage: "Obra eliminada con éxito.",
          });
          setOpenDeleteDialog(false);
          setSelectedObra(null);
          setRefresh(true);
        }
      })
      .catch((error) => {
        alertError({
          error,
          customMessage: "No se ha podido eliminar la obra.",
        });
      });
  };

  const handleOpenObraEdit = (obraId) => {
    history.push({
      pathname: `/obras/${obraId}/edit`,
      state: {
        page: getCurrentTablePage(location),
      },
    });
  };

  function buttonFormatter(obraId) {
    const obra = data.find((item) => item.id === obraId);

    return (
      <>
        <Tooltip title="Editar">
          <Button
            style={buttonsStyle}
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              handleOpenObraEdit(obraId);
            }}
          >
            <EditIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Eliminar obra">
          <Button
            style={buttonsStyle}
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedObra(obra);
              setOpenDeleteDialog(true);
            }}
          >
            <DeleteIcon />
          </Button>
        </Tooltip>
      </>
    );
  }

  const columns = [
    { dataField: "codigoObra", text: "Código obra", sort: true },
    { dataField: "numOferta", text: "Oferta origen", sort: true },
    {
      dataField: "cliente",
      text: "Cliente",
      sort: true,
      headerAlign: "center",
      align: "center",
    },
    {
      dataField: "localizacion",
      text: "Localización",
      sort: true,
      headerAlign: "center",
      align: "center",
    },
    {
      dataField: "fechaInicio",
      text: "Fecha inicio",
      sort: true,
      headerAlign: "center",
      align: "center",
    },
    {
      dataField: "fechaFin",
      text: "Fecha fin",
      sort: true,
      headerAlign: "center",
      align: "center",
    },
    {
      dataField: "estado",
      text: "Estado",
      sort: true,
      headerAlign: "center",
      align: "center",
      formatter: obraStatusFormatter,
    },
    {
      dataField: "situacion",
      text: "Planificación",
      sort: true,
      headerAlign: "center",
      align: "center",
      formatter: planningStatusFormatter,
    },
    {
      dataField: "progreso",
      text: "Progreso",
      sort: true,
      headerAlign: "center",
      align: "center",
    },
    { dataField: "id", text: "", formatter: buttonFormatter },
  ];

  const rowEvents = {
    onClick: (event, row) => {
      handleOpenObraEdit(row.id);
    },
  };

  const rowStyle = { cursor: "pointer" };

  const renderFiltersContent = () => (
    <div className="d-sm-flex w-100">
      <Autocomplete
        className="w-100 mr-5"
        id="autocomplete-obra-customer"
        options={customers}
        getOptionLabel={(option) => option?.nombre || ""}
        value={filterOptions.cliente}
        onChange={(event, selected) => {
          setFilterOptions({
            ...filterOptions,
            cliente: selected,
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            margin="normal"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            label="Cliente"
          />
        )}
      />
      <Autocomplete
        className="w-100"
        id="autocomplete-obra-status"
        options={obraStatus}
        getOptionLabel={(option) => option?.descripcion || ""}
        value={filterOptions.estado}
        onChange={(event, selected) => {
          setFilterOptions({
            ...filterOptions,
            estado: selected,
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            margin="normal"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            label="Estado"
          />
        )}
      />
    </div>
  );

  return (
    <>
      <Card>
        <CardHeader headerClassName="px-0">
          <CardHeader
            title="Obras"
            sticky={false}
            headerClassName="d-flex justify-content-between w-100 py-3"
          >
            <CardHeaderToolbar>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleOpenCreateDialog}
              >
                Nueva obra
              </button>
            </CardHeaderToolbar>
          </CardHeader>
          <div className="w-100">
            <FiltersCard
              collapsable={false}
              filtersContent={renderFiltersContent}
              collapsed={collapsed}
              setCollapsed={setCollapsed}
              handleClearFilters={handleClearFilters}
              handleSearch={handleSearch}
            />
          </div>
        </CardHeader>
        <CardBody>
          <Table
            data={getData(filteredData, customers)}
            columns={columns}
            rowEvents={rowEvents}
            rowStyle={rowStyle}
          />
        </CardBody>
      </Card>

      <Dialog
        open={openCreateDialog}
        onClose={() => !creatingObra && setOpenCreateDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Nueva obra</DialogTitle>
        <DialogContent>
          <div className="d-flex align-items-center mb-4">
            <AssignmentIcon className="mr-2" />
            <span>Selecciona una oferta aprobada disponible como origen.</span>
          </div>
          <Autocomplete
            id="autocomplete-available-offers"
            options={offerOptions}
            getOptionLabel={(option) => option?.label || ""}
            value={selectedOffer}
            onChange={(event, selected) => setSelectedOffer(selected)}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                variant="outlined"
                label="Oferta aprobada"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
          <div className="row">
            <div className="col-md-4">
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                label="Código obra"
                value={newObraData.codigo_obra}
                onChange={(event) =>
                  setNewObraData({
                    ...newObraData,
                    codigo_obra: event.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
              />
            </div>
            <div className="col-md-4">
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                type="date"
                label="Fecha inicio"
                value={newObraData.fecha_inicio}
                onChange={(event) =>
                  setNewObraData({
                    ...newObraData,
                    fecha_inicio: event.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
              />
            </div>
            <div className="col-md-4">
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                type="date"
                label="Fecha fin"
                value={newObraData.fecha_fin}
                onChange={(event) =>
                  setNewObraData({
                    ...newObraData,
                    fecha_fin: event.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
              />
            </div>
          </div>
          {!offerOptions.length && (
            <div className="mt-3 text-muted">
              No hay ofertas aprobadas disponibles para crear una obra.
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenCreateDialog(false)}
            color="default"
            disabled={creatingObra}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreateObra}
            color="primary"
            variant="contained"
            disabled={
              !selectedOffer ||
              !newObraData.codigo_obra?.trim() ||
              !newObraData.fecha_inicio ||
              !newObraData.fecha_fin ||
              creatingObra
            }
          >
            Crear obra
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        title={"¿Estás seguro de que quieres eliminar esta obra?"}
        open={openDeleteDialog}
        setOpen={setOpenDeleteDialog}
        onConfirm={handleDeleteObra}
      />
    </>
  );
}
